import React, { useEffect, useState } from 'react';
import imageCacheService from '../utils/imageCacheService';

/**
 * CachedImage Component
 *
 * An image component that automatically uses cached images when available
 * Falls back to original URL if caching fails
 */
const CachedImage = ({
  src,
  alt = '',
  className = '',
  style,
  onLoad,
  loading = 'lazy',
  ...otherProps
}) => {
  const [imageSrc, setImageSrc] = useState(src);
  const [isLoading, setIsLoading] = useState(true);
  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    let mounted = true;
    let objectUrl = null;

    const loadImage = async () => {
      try {
        // Initialize cache service
        if (!imageCacheService.isInitialized) {
          await imageCacheService.init();
        }

        // Get cached image blob
        const blob = await imageCacheService.getImage(src);

        if (!mounted) return;

        if (blob) {
          // Create object URL from blob
          objectUrl = URL.createObjectURL(blob);
          setBlobUrl(objectUrl);
          setImageSrc(objectUrl);
        } else {
          // Fallback to original URL
          setImageSrc(src);
        }
      } catch (error) {
        console.error('Failed to load cached image:', error);
        if (mounted) {
          setImageSrc(src); // Fallback to original URL
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    loadImage();

    // Cleanup: revoke blob URL on unmount
    return () => {
      mounted = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [src]);

  return (
    <img
      className={className}
      src={imageSrc}
      alt={alt}
      loading={loading}
      onLoad={onLoad}
      style={style}
      {...otherProps}
    />
  );
};

export default CachedImage;
